/**
 * AppICEMAP — Report expiry
 *
 * Reports are meant to live for `category.ttl_minutes` and then disappear, but
 * nothing was ever filling `expires_at`: neither the app (it is not in the
 * create payload) nor the backend (no Directus flow), so every record has it
 * null and nothing ever expired.
 *
 * Until a server-side flow sets it, the expiry is DERIVED here from
 * `created_at + ttl_minutes`. That fixes the reports already stored with a null
 * `expires_at` too, and needs no write permission on the field.
 */

/** Fallback lifetime when the category carries no TTL */
const DEFAULT_TTL_MINUTES = 240;

/**
 * When does this report expire?
 * @param {object} report
 * @returns {number | null} epoch ms, or null when it cannot be determined
 */
export function getExpiresAt(report) {
	if (!report) return null;

	// Trust the stored value when the backend does fill it in
	if (report.expires_at) {
		const stored = new Date(report.expires_at).getTime();
		if (Number.isFinite(stored)) return stored;
	}

	const created = new Date(report.created_at || report.date_created).getTime();
	if (!Number.isFinite(created)) return null;

	const ttl = report.category?.ttl_minutes || DEFAULT_TTL_MINUTES;
	return created + ttl * 60_000;
}

/**
 * Has the report outlived its TTL?
 * @param {object} report
 * @param {number} [now]
 * @returns {boolean}
 */
export function isExpired(report, now = Date.now()) {
	if (report?.status === "expired") return true;
	const expires = getExpiresAt(report);
	return expires !== null && expires <= now;
}

/**
 * How much of its life is left, 1 (just posted) → 0 (expired).
 * @param {object} report
 * @param {number} [now]
 * @returns {number}
 */
export function getRemainingFraction(report, now = Date.now()) {
	const expires = getExpiresAt(report);
	const created = new Date(
		report?.created_at || report?.date_created,
	).getTime();

	if (expires === null || !Number.isFinite(created)) return 1;

	const total = expires - created;
	if (total <= 0) return 0;

	return Math.max(0, Math.min(1, (expires - now) / total));
}

/**
 * Drop reports past their TTL.
 * @param {Array} reports
 * @returns {Array}
 */
export function filterActive(reports) {
	if (!Array.isArray(reports)) return [];
	const now = Date.now();
	return reports.filter((r) => !isExpired(r, now));
}

/**
 * Human countdown, e.g. "caduca en 12 min".
 * @param {object} report
 * @returns {string}
 */
export function formatTimeLeft(report) {
	const expires = getExpiresAt(report);
	if (expires === null) return "";

	const mins = Math.round((expires - Date.now()) / 60_000);
	if (mins <= 0) return "caducado";
	if (mins < 60) return `caduca en ${mins} min`;

	const hours = Math.round(mins / 60);
	if (hours < 24) return `caduca en ${hours} h`;

	return `caduca en ${Math.round(hours / 24)} d`;
}
